import { formatBytes } from "gfx/MeshDataStats";
import { WebGLMultiContextResourceTracker } from "gfx/WebGLContextResourceTracker";
import { useEffect, useRef, useState } from "react";

function pickGridLineInterval(peakStat: number) {
  const orderOfMagnitude = Math.floor(Math.log10(peakStat));
  const intervals = [0.1, 0.2, 0.5, 1, 2, 5];
  for (let i = 0; i < intervals.length; i++) {
    if (intervals[i] * 10 ** orderOfMagnitude >= peakStat / 5) {
      return intervals[i] * 10 ** orderOfMagnitude;
    }
  }
  return 1 * 10 ** orderOfMagnitude;
}

export function GLMemoryHistoryGraph() {
  const glMemoryCanvasContainer = useRef<HTMLDivElement>(null);
  const glMemoryCanvasRef = useRef<HTMLCanvasElement>(null);
  const [totalMemoryUsage, setTotalMemoryUsage] = useState(0);
  const [peakMemoryUsage, setPeakMemoryUsage] = useState(0);

  function yFromMemoryUse(memoryUsage: number, currentPeakMemoryUsage: number, height: number) {
    const relative = memoryUsage / currentPeakMemoryUsage;
    const y = (1 - relative) * (height - 2) + 1;
    return Math.round(y);
  }

  function drawGraph(ctx: CanvasRenderingContext2D, currentPeakMemoryUsage: number, width: number, height: number) {
    ctx.strokeStyle = "blue";
    ctx.lineWidth = 2;
    const history = WebGLMultiContextResourceTracker.instance?.getHistory();
    if (history !== undefined) {
      const yFromHistoryMemoryUse = (history, index: number) => {
        const value = history.totalMemoryUsage[history.totalMemoryUsage.length - 1 - index];
        return yFromMemoryUse(value, currentPeakMemoryUsage, height);
      };

      ctx.beginPath();
      ctx.moveTo(width - 1, yFromHistoryMemoryUse(history, 0));
      for (let i = 1; i < Math.min(history.totalMemoryUsage.length, width); i++) {
        ctx.lineTo(width - i - 1, yFromHistoryMemoryUse(history, i));
      }
      ctx.stroke();
    }
  }

  function drawGridLines(ctx: CanvasRenderingContext2D, currentPeakMemoryUsage: number, width: number, height: number) {
    const gridLineInterval = pickGridLineInterval(currentPeakMemoryUsage);
    const numGridLines = Math.ceil(currentPeakMemoryUsage / gridLineInterval) + 1;
    ctx.strokeStyle = "#aaa";
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let i = 0; i < numGridLines; i++) {
      const lineMemory = i * gridLineInterval;
      const y = yFromMemoryUse(lineMemory, currentPeakMemoryUsage, height);
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
    }
    ctx.stroke();

    ctx.fillStyle = "#000";
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    for (let i = 0; i < numGridLines; i++) {
      const lineMemory = i * gridLineInterval;
      const y = yFromMemoryUse(lineMemory, currentPeakMemoryUsage, height);
      ctx.fillText(formatBytes(lineMemory), 0, y);
    }
  }

  function refreshCanvas(currentPeakMemoryUsage: number) {
    if (glMemoryCanvasRef.current && glMemoryCanvasContainer.current) {
      const width = glMemoryCanvasContainer.current.getBoundingClientRect().width;
      const height = 100;
      if (glMemoryCanvasRef.current.width !== width || glMemoryCanvasRef.current.height !== height) {
        glMemoryCanvasRef.current.width = width;
        glMemoryCanvasRef.current.height = height;
      }
      const ctx = glMemoryCanvasRef.current.getContext("2d");
      if (ctx) {
        // Clear the canvas
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, width, height);

        // Room for labels
        const leftMargin = 50;
        const verticalMargin = 10;

        ctx.save();
        ctx.translate(leftMargin, verticalMargin);
        drawGridLines(ctx, currentPeakMemoryUsage, width - leftMargin, height - verticalMargin * 2);
        drawGraph(ctx, currentPeakMemoryUsage, width - leftMargin, height - verticalMargin * 2);
        ctx.restore();
      }
    }
  }

  function refresh() {
    const currentTotalMemoryUsage = WebGLMultiContextResourceTracker.instance?.getTotalMemoryUsage();
    const currentPeakMemoryUsage = WebGLMultiContextResourceTracker.instance?.getPeakMemoryUsage();
    if (currentTotalMemoryUsage) {
      setTotalMemoryUsage(currentTotalMemoryUsage);
    }
    if (currentPeakMemoryUsage) {
      setPeakMemoryUsage(currentPeakMemoryUsage);
    }
    refreshCanvas(currentPeakMemoryUsage ?? peakMemoryUsage);
  }

  useEffect(() => {
    refresh();
    const interval = setInterval(() => {
      refresh();
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  return (
    <>
      <div style={{ padding: "1em" }} ref={glMemoryCanvasContainer}>
        <canvas ref={glMemoryCanvasRef} style={{ border: "1px solid #777", width: "100%", height: "100px" }} />
      </div>

      <div style={{ padding: "1em" }}>
        Current = {formatBytes(totalMemoryUsage)}
        <br />
        Peak = {formatBytes(peakMemoryUsage)}
      </div>
    </>
  );
}
